import dayjs from 'dayjs'

import { SpotPrice } from './useDayAheadPrices'
import { meanAvg } from '../../utils/math'
import { TimePeriodResolution } from '../../utils/timePeriod'

/**
 * Downsample day-ahead prices to a lower resolution by mean averaging.
 * 
 * @example
 * ```ts
 * const now = dayjs()
 * 
 * // Fetch 96 samples of 15-minute resolution
 * const todayPrices = useDayAheadPrices(now.startOf('day'), now.endOf('day'))
 * 
 * // Conver to 24 samples of 1-hour resolution
 * const hourlyPrices = meanAggregateDayAheadPrices(todayPrices, 'hour')
 * ```
 */
export const meanAggregateDayAheadPrices = (
    dayAheadPrices: SpotPrice[],
    targetResolution: TimePeriodResolution,
): SpotPrice[] => {
    if (dayAheadPrices.length === 0) {
        return []
    }

    if (targetResolution === 'quarterHour') {
        return dayAheadPrices
    }

    /**
     * Key = Unix timestamp for the start of the resolution period.
     * Value = Higher resolution (e.g. 15-minute) prices during that resolution period.
     */
    const pricesByTime = new Map<number, number[]>()
    for (const price of dayAheadPrices) {
        const startTime = price.deliveryStart.startOf(targetResolution).valueOf()
        const existingPrices = pricesByTime.get(startTime) ?? []
        pricesByTime.set(startTime, [...existingPrices, price.centsPerKwh])
    }

    const aggregatedPrices = Array.from(pricesByTime, ([startTime, prices]) => ({
        deliveryStart: dayjs(startTime),
        deliveryEnd: dayjs(startTime).endOf(targetResolution),
        centsPerKwh: meanAvg(prices),
    }))

    return aggregatedPrices.sort(
        (a, b) => a.deliveryStart.valueOf() - b.deliveryStart.valueOf()
    )
}
